import React from 'react'
import { useState, useCallback, useEffect, useRef } from 'react'
import { generateShapeId } from '../utils/shapeUtils'
import { rawShapesToShapes } from '../utils/pointsConverter'
import { Shape } from '../types'

// Image data structure
export interface ImageData {
  id: string
  name: string
  src: string
  shapes: Shape[]
  history: Shape[][]
  historyIndex: number
  initialShapes?: Shape[] // Optional initial shapes passed from parent
  deletedDbIds: (number | string)[] // Track database IDs of deleted shapes for backend sync
}


export function useMultiImageShapes(
  customImages?: Omit<ImageData, 'shapes' | 'history' | 'historyIndex'>[],
  initialCurrentImageId?: string,
  /** When this value changes, current frame's shapes are replaced with prop initialShapes (e.g. after refetch). Avoids remount for a smooth update. */
  syncShapesVersion?: number
) {
  // Use custom images if provided, otherwise fall back to default IMAGES
  const imagesToUse = customImages || []

  // Helper: initialize image data keyed by *index* so duplicate ids still get separate frames
  const initializeImageDataByIndex = (images: typeof imagesToUse): Record<number, ImageData> => {
    const initialData: Record<number, ImageData> = {}
    images.forEach((img, index) => {
      const shapesToLoad = (img.initialShapes || []).map(s => ({
        ...s,
        isNew: false,
        isDirty: false
      }))
      initialData[index] = {
        ...img,
        shapes: shapesToLoad,
        history: [shapesToLoad],
        historyIndex: 0,
        deletedDbIds: []
      }
    })
    return initialData
  }

  const [imagesData, setImagesData] = useState<Record<number, ImageData>>(() => initializeImageDataByIndex(imagesToUse))

  // Navigate by *index* so next/prev work even when frame ids are duplicated
  const [currentFrameIndex, setCurrentFrameIndex] = useState<number>(() => {
    if (imagesToUse.length === 0) return 0
    if (initialCurrentImageId) {
      const idx = imagesToUse.findIndex(img => img.id === initialCurrentImageId)
      if (idx >= 0) return idx
    }
    return 0
  })

  // Derive current image id from index (for parent/API; may repeat if ids are duplicated)
  const currentImageId = imagesToUse[currentFrameIndex]?.id ?? ''

  // Keep index in bounds when list changes
  useEffect(() => {
    if (imagesToUse.length === 0) return
    setCurrentFrameIndex(prev => Math.min(prev, imagesToUse.length - 1))
  }, [imagesToUse.length])

  // Sync index when initialCurrentImageId is set (e.g. after save remount). Depend only on stable
  // imageIdsKey + initialCurrentImageId so we don't re-run when parent passes new array reference
  // (avoids infinite loop when add/remove filter).
  const imageIdsKey = imagesToUse.length === 0 ? '' : imagesToUse.map(img => img.id).join('\0')
  const imagesToUseRef = useRef(imagesToUse)
  imagesToUseRef.current = imagesToUse
  useEffect(() => {
    const list = imagesToUseRef.current
    if (list.length === 0 || !initialCurrentImageId) return
    const idx = list.findIndex(img => img.id === initialCurrentImageId)
    if (idx < 0) return
    setCurrentFrameIndex(idx)
  }, [initialCurrentImageId, imageIdsKey])

  // Fingerprint shape geometry so sync key changes when API returns new points (e.g. after edit in normal viewer).
  const shapeGeometryKey = (s: any) => {
    if (!s) return ''
    if (s.type === 'circle' && s.center) return `c:${s.center.x},${s.center.y},${s.radiusX},${s.radiusY}`
    if ((s.type === 'line' || s.type === 'ruler') && s.startPoint && s.endPoint) return `l:${s.startPoint.x},${s.startPoint.y},${s.endPoint.x},${s.endPoint.y}`
    if (s.type === 'square' && s.startPoint && s.endPoint) return `s:${s.startPoint.x},${s.startPoint.y},${s.endPoint.x},${s.endPoint.y}`
    if (s.type === 'freehand' && Array.isArray(s.points)) return `f:${s.points.length},${s.points[0]?.x},${s.points[0]?.y},${s.points[s.points.length - 1]?.x},${s.points[s.points.length - 1]?.y}`
    return String(s.id ?? s.dbId ?? '')
  }
  // Update imagesData when refetched data (initialShapes) changes — do NOT run when only currentFrameIndex changes.
  // Include geometry in key so when API returns new points the cache is invalidated and drawing updates.
  const imagesDataSyncKey = imagesToUse.length === 0 ? '' : imagesToUse.map((img, i) => ({
    i,
    id: img.id,
    shapesCount: img.initialShapes?.length || 0,
    shapeKey: (img.initialShapes || []).map((s: any) => `${s.dbId ?? s.id ?? ''}:${shapeGeometryKey(s)}`).join(';')
  })).map(x => `${x.i}:${x.id}:${x.shapesCount}:${x.shapeKey}`).join('|')
  useEffect(() => {
    if (imagesToUse.length === 0) return
    setImagesData(prevData => {
      const newData = { ...prevData }
      imagesToUse.forEach((img, index) => {
        const cur = prevData[index]
        // Only overwrite slot if it's the same frame (id match) so refetch never mixes up frames when order changes.
        // When the slot gets a different frame id (e.g. virtualized window shifted), always overwrite so segmentation updates with the new frame.
        const sameFrame = !cur || (cur as any).id === img.id
        const initialCount = img.initialShapes?.length || 0
        const currentCount = cur?.shapes?.length ?? 0
        const userHasAddedShapes = cur && currentCount > initialCount
        const isCurrentFrame = index === currentFrameIndex
        const wouldLoseShapes = isCurrentFrame && cur && currentCount > (img.initialShapes?.length ?? 0)
        const hasUnsavedNewShapes = cur?.shapes?.some((s: any) => (s as any).isNew) ?? false
        const hasUnsavedDirtyShapes = cur?.shapes?.some((s: any) => (s as any).isDirty) ?? false
        const serverSentFewer = initialCount < currentCount
        const shouldOverwriteSameFrame =
          sameFrame &&
          !hasUnsavedDirtyShapes &&
          (!cur ||
            ((cur.historyIndex === 0 && !userHasAddedShapes) && !wouldLoseShapes) ||
            (serverSentFewer && !hasUnsavedNewShapes))
        const slotHasNewFrame = cur && (cur as any).id !== img.id
        if (shouldOverwriteSameFrame || slotHasNewFrame) {
          const shapesToLoad = (img.initialShapes || []).map((s: any) => ({
            ...s,
            isNew: false,
            isDirty: false
          }))
          newData[index] = {
            ...img,
            shapes: shapesToLoad,
            history: [shapesToLoad],
            historyIndex: 0,
            deletedDbIds: slotHasNewFrame ? [] : (cur?.deletedDbIds ?? [])
          }
        } else if (cur) {
          newData[index] = {
            ...prevData[index],
            src: img.src,
            name: img.name
          }
        }
      })
      return newData
    })
  }, [imagesDataSyncKey])

  // When parent bumps this after refetch, replace current frame's shapes with new initialShapes (no remount = smooth).
  const lastSyncVersionRef = useRef(0)
  useEffect(() => {
    if (syncShapesVersion == null || syncShapesVersion === 0 || imagesToUse.length === 0) return
    if (lastSyncVersionRef.current === syncShapesVersion) return
    lastSyncVersionRef.current = syncShapesVersion
    const img = imagesToUse[currentFrameIndex]
    if (!img) return
    const shapesToLoad = (img.initialShapes || []).map(s => ({ ...s, isNew: false, isDirty: false }))
    setImagesData(prev => {
      const cur = prev[currentFrameIndex]
      if (!cur) return prev
      return {
        ...prev,
        [currentFrameIndex]: {
          ...cur,
          shapes: shapesToLoad,
          history: [shapesToLoad],
          historyIndex: 0,
        }
      }
    })
  }, [syncShapesVersion, currentFrameIndex, imagesToUse])

  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null)
  const shouldAutoSave = useRef(false)

  const currentImage = imagesData[currentFrameIndex]
  const shapes = currentImage?.shapes ?? []
  const history = currentImage?.history ?? [[]]
  const historyIndex = currentImage?.historyIndex ?? 0

  const goToImage = useCallback((imageId: string) => {
    const idx = imagesToUse.findIndex(img => img.id === imageId)
    if (idx >= 0) {
      setCurrentFrameIndex(idx)
      setSelectedShapeId(null)
    }
  }, [imagesToUse])

  const nextImage = useCallback(() => {
    const total = imagesToUse.length
    if (total === 0) return
    setCurrentFrameIndex(prev => {
      const next = Math.min(prev + 1, total - 1)
      if (next !== prev) setSelectedShapeId(null)
      return next
    })
  }, [imagesToUse.length])

  const previousImage = useCallback(() => {
    setCurrentFrameIndex(prev => {
      const next = Math.max(prev - 1, 0)
      if (next !== prev) setSelectedShapeId(null)
      return next
    })
  }, [])

  const addShape = useCallback((shape: Omit<Shape, 'id'>) => {
    const newShape: Shape = {
      ...shape,
      id: generateShapeId(),
      isNew: true,
      isDirty: false,
      isSelected: false
    } as Shape
    setImagesData(prev => ({
      ...prev,
      [currentFrameIndex]: {
        ...prev[currentFrameIndex],
        shapes: [...(prev[currentFrameIndex]?.shapes ?? []), newShape]
      }
    }))
    return newShape.id
  }, [currentFrameIndex])

  const updateShape = useCallback((id: string, updates: Partial<Shape>) => {
    setImagesData(prev => {
      const cur = prev[currentFrameIndex]
      if (!cur) return prev
      return {
        ...prev,
        [currentFrameIndex]: {
          ...cur,
          shapes: cur.shapes.map(shape =>
            shape.id === id
              ? ({ ...shape, ...updates, isNew: shape.isNew, isDirty: shape.isNew ? false : true } as Shape)
              : shape
          )
        }
      }
    })
  }, [currentFrameIndex])

  /** Append new shapes to the current frame (e.g. derived BPD line for HC ellipse). */
  const appendShapes = useCallback(
    (newShapes: Shape[]) => {
      if (!newShapes.length) return;
      setImagesData((prev) => {
        const cur = prev[currentFrameIndex];
        if (!cur) return prev;
        const existingIds = new Set(cur.shapes.map((s) => s.id));
        const toAdd = newShapes.filter((s) => s.id && !existingIds.has(s.id));
        if (!toAdd.length) return prev;
        const nextShapes = [...cur.shapes, ...toAdd];
        return {
          ...prev,
          [currentFrameIndex]: {
            ...cur,
            shapes: nextShapes,
            history: [...cur.history.slice(0, cur.historyIndex + 1), nextShapes],
            historyIndex: cur.historyIndex + 1,
          },
        };
      });
    },
    [currentFrameIndex]
  );

  /** Apply multiple shape patches in one state update (BPD ↔ HC sync). */
  const updateShapesBatch = useCallback(
    (patches: Map<string, Partial<Shape>>) => {
      if (patches.size === 0) return;
      setImagesData((prev) => {
        const cur = prev[currentFrameIndex];
        if (!cur) return prev;
        return {
          ...prev,
          [currentFrameIndex]: {
            ...cur,
            shapes: cur.shapes.map((shape) => {
              const patch = patches.get(shape.id);
              if (!patch) return shape;
              return {
                ...shape,
                ...patch,
                isNew: shape.isNew,
                isDirty: shape.isNew ? false : true,
              } as Shape;
            }),
          },
        };
      });
    },
    [currentFrameIndex]
  );

  const deleteShape = useCallback((id: string) => {
    setImagesData(prev => {
      const cur = prev[currentFrameIndex]
      if (!cur) return prev
      const shapeToDelete = cur.shapes.find(shape => shape.id === id)
      const newDeletedDbIds = [...cur.deletedDbIds]
      if (shapeToDelete && (shapeToDelete as any).dbId && !newDeletedDbIds.includes((shapeToDelete as any).dbId)) {
        newDeletedDbIds.push((shapeToDelete as any).dbId)
      }
      return {
        ...prev,
        [currentFrameIndex]: {
          ...cur,
          shapes: cur.shapes.filter(shape => shape.id !== id),
          deletedDbIds: newDeletedDbIds
        }
      }
    })
    if (selectedShapeId === id) setSelectedShapeId(null)
  }, [currentFrameIndex, selectedShapeId])

  const clearDirtyStates = useCallback(() => {
    setImagesData(prev => {
      const cur = prev[currentFrameIndex]
      if (!cur) return prev
      return {
        ...prev,
        [currentFrameIndex]: {
          ...cur,
          shapes: cur.shapes.map(shape => ({ ...shape, isNew: false, isDirty: false }))
        }
      }
    })
  }, [currentFrameIndex])

  const clearShapes = useCallback(() => {
    setImagesData(prev => {
      const cur = prev[currentFrameIndex]
      if (!cur) return prev
      return { ...prev, [currentFrameIndex]: { ...cur, shapes: [] } }
    })
    setSelectedShapeId(null)
  }, [currentFrameIndex])

  const selectShape = useCallback((id: string | null) => {
    setImagesData(prev => {
      const cur = prev[currentFrameIndex]
      if (!cur) return prev
      const shapes = id
        ? cur.shapes.map(shape => ({ ...shape, isSelected: shape.id === id }))
        : cur.shapes.map(shape => ({ ...shape, isSelected: false }))
      return { ...prev, [currentFrameIndex]: { ...cur, shapes } }
    })
    setSelectedShapeId(id)
  }, [currentFrameIndex])

  // Get selected shape
  const getSelectedShape = useCallback(() => {
    return shapes.find(shape => shape.id === selectedShapeId) || null
  }, [shapes, selectedShapeId])

  const saveToHistory = useCallback(() => {
    const cur = imagesData[currentFrameIndex]
    if (!cur) return
    const lastSavedState = cur.history[cur.historyIndex]
    const currentStateString = JSON.stringify(cur.shapes)
    const lastStateString = lastSavedState ? JSON.stringify(lastSavedState) : ''
    if (currentStateString === lastStateString) return
    const newHistory = cur.history.slice(0, cur.historyIndex + 1)
    // Deep clone so undo/redo restores independent snapshots and doesn't skip steps
    newHistory.push(JSON.parse(currentStateString))
    // Cap history to 5 steps per frame so memory stays under ~2 GB with many frames
    const MAX_HISTORY_LENGTH = 5
    if (newHistory.length > MAX_HISTORY_LENGTH) {
      newHistory.splice(0, newHistory.length - MAX_HISTORY_LENGTH)
    }
    setImagesData(prev => ({
      ...prev,
      [currentFrameIndex]: { ...prev[currentFrameIndex], history: newHistory, historyIndex: newHistory.length - 1 }
    }))
  }, [imagesData, currentFrameIndex])

  /** Remove from deletedDbIds any id that appears in restored shapes (so undo/redo don't send them as delete_segmentation_ids). */
  const reconcileDeletedDbIds = useCallback((deletedDbIds: (number | string)[], restoredShapes: Shape[]) => {
    const restoredIds = new Set(restoredShapes.map((s: any) => (s.dbId != null ? String(s.dbId) : null)).filter(Boolean))
    return deletedDbIds.filter((id) => !restoredIds.has(String(id)))
  }, [])

  const undo = useCallback(() => {
    const cur = imagesData[currentFrameIndex]
    if (!cur || cur.historyIndex <= 0) return
    const previousState = cur.history[cur.historyIndex - 1]
    // Use snapshot from history (already deep-cloned when saved) so undo is reliable
    const restored = Array.isArray(previousState) ? previousState : []
    const reconciledDeleted = reconcileDeletedDbIds(cur.deletedDbIds, restored)
    setImagesData(prev => ({
      ...prev,
      [currentFrameIndex]: {
        ...prev[currentFrameIndex],
        shapes: restored,
        historyIndex: cur.historyIndex - 1,
        deletedDbIds: reconciledDeleted,
      },
    }))
  }, [imagesData, currentFrameIndex, reconcileDeletedDbIds])

  const redo = useCallback(() => {
    const cur = imagesData[currentFrameIndex]
    if (!cur || cur.historyIndex >= cur.history.length - 1) return
    const nextState = cur.history[cur.historyIndex + 1]
    const restored = Array.isArray(nextState) ? nextState : []
    const reconciledDeleted = reconcileDeletedDbIds(cur.deletedDbIds, restored)
    setImagesData(prev => ({
      ...prev,
      [currentFrameIndex]: {
        ...prev[currentFrameIndex],
        shapes: restored,
        historyIndex: cur.historyIndex + 1,
        deletedDbIds: reconciledDeleted,
      },
    }))
  }, [imagesData, currentFrameIndex, reconcileDeletedDbIds])

  // Auto-save to history when shapes change (if flagged)
  useEffect(() => {
    if (shouldAutoSave.current) {
      shouldAutoSave.current = false
      saveToHistory()
    }
  }, [shapes, saveToHistory])

  /** Call after an async state update (e.g. updateShape) so the next shapes change pushes to history. Fixes redo when replacing a structure. */
  const requestSaveToHistory = useCallback(() => {
    shouldAutoSave.current = true
  }, [])

  const addShapeWithAutoSave = useCallback((shape: Omit<Shape, 'id'>): Shape => {
    const newShape = {
      ...shape,
      id: generateShapeId(),
      isNew: true,
      isDirty: false,
    } as Shape
    setImagesData(prev => {
      const cur = prev[currentFrameIndex]
      if (!cur) return prev
      return {
        ...prev,
        [currentFrameIndex]: { ...cur, shapes: [...cur.shapes, newShape] }
      }
    })
    shouldAutoSave.current = true
    return newShape
  }, [currentFrameIndex])

  const deleteShapeWithAutoSave = useCallback((id: string): Shape | null => {
    let deletedShape: Shape | null = null
    setImagesData(prev => {
      const cur = prev[currentFrameIndex]
      if (!cur) return prev
      const shapeToDelete = cur.shapes.find(shape => shape.id === id)
      deletedShape = shapeToDelete || null
      const newDeletedDbIds = [...cur.deletedDbIds]
      if (shapeToDelete && (shapeToDelete as any).dbId && !newDeletedDbIds.includes((shapeToDelete as any).dbId)) {
        newDeletedDbIds.push((shapeToDelete as any).dbId)
      }
      return {
        ...prev,
        [currentFrameIndex]: {
          ...cur,
          shapes: cur.shapes.filter(shape => shape.id !== id),
          deletedDbIds: newDeletedDbIds
        }
      }
    })
    if (selectedShapeId === id) setSelectedShapeId(null)
    shouldAutoSave.current = true
    return deletedShape
  }, [currentFrameIndex, selectedShapeId])

  const getDeletedDbIds = useCallback((): (number | string)[] => {
    return currentImage?.deletedDbIds || []
  }, [currentImage])

  const getDeletedDbIdsForImage = useCallback((imageId: string): (number | string)[] => {
    const idx = imagesToUse.findIndex(img => img.id === imageId)
    return idx >= 0 ? (imagesData[idx]?.deletedDbIds || []) : []
  }, [imagesData, imagesToUse])

  const clearDeletedDbIds = useCallback((imageId?: string) => {
    const idx = imageId != null ? imagesToUse.findIndex(img => img.id === imageId) : currentFrameIndex
    if (idx < 0) return
    setImagesData(prev => {
      const cur = prev[idx]
      if (!cur) return prev
      return { ...prev, [idx]: { ...cur, deletedDbIds: [] } }
    })
  }, [currentFrameIndex, imagesToUse])

  const deleteShapeByLabel = useCallback((label: string) => {
    const cleanLabel = label.toLowerCase().replace(/\s*not\s*visible\s*/gi, '').trim()
    setImagesData(prev => {
      const cur = prev[currentFrameIndex]
      if (!cur) return prev
      const toDelete = cur.shapes.filter(shape => {
        const sl = (shape.label || '').toLowerCase().replace(/\s*not\s*visible\s*/gi, '').trim()
        const sb = ((shape as any).backendLabel || '').toLowerCase().replace(/\s*not\s*visible\s*/gi, '').replace(/_/g, ' ').trim()
        return sl === cleanLabel || sb === cleanLabel
      })
      const newDeletedDbIds = [...cur.deletedDbIds]
      toDelete.forEach(shape => {
        if ((shape as any).dbId && !newDeletedDbIds.includes((shape as any).dbId)) newDeletedDbIds.push((shape as any).dbId)
      })
      const shapes = cur.shapes.filter(shape => {
        const sl = (shape.label || '').toLowerCase().replace(/\s*not\s*visible\s*/gi, '').trim()
        const sb = ((shape as any).backendLabel || '').toLowerCase().replace(/\s*not\s*visible\s*/gi, '').replace(/_/g, ' ').trim()
        return sl !== cleanLabel && sb !== cleanLabel
      })
      return { ...prev, [currentFrameIndex]: { ...cur, shapes, deletedDbIds: newDeletedDbIds } }
    })
    shouldAutoSave.current = true
  }, [currentFrameIndex])

  const canGoPrev = currentFrameIndex > 0
  const canGoNextVal = currentFrameIndex < imagesToUse.length - 1
  const totalImagesVal = imagesToUse.length
  const currentImageIndexVal = currentFrameIndex + 1

  const goToIndex = useCallback((index: number) => {
    const i = Math.max(0, Math.min(index, imagesToUse.length - 1))
    setCurrentFrameIndex(i)
    setSelectedShapeId(null)
  }, [imagesToUse.length])

  return {
    shapes,
    selectedShapeId,
    currentImageId,
    currentImageSrc: imagesToUse[currentFrameIndex]?.src ?? currentImage?.src,
    currentImageName: currentImage?.name,
    clearDirtyStates,
    goToImage,
    goToIndex,
    nextImage,
    previousImage,
    canGoPrevious: canGoPrev,
    canGoNext: canGoNextVal,
    totalImages: totalImagesVal,
    currentImageIndex: currentImageIndexVal,
    
    // Shape operations (on current image)
    addShape,
    appendShapes,
    updateShape,
    updateShapesBatch,
    deleteShape,
    clearShapes,
    selectShape,
    getSelectedShape,
    deleteShapeByLabel,
    
    // History operations (on current image)
    saveToHistory,
    requestSaveToHistory,
    undo,
    redo,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
    
    // Auto-save operations
    addShapeWithAutoSave,
    deleteShapeWithAutoSave,
    
    // Deleted IDs tracking (for backend sync)
    getDeletedDbIds,
    getDeletedDbIdsForImage,
    clearDeletedDbIds,
    
    // All images metadata
    allImages: imagesToUse
  }
}

